import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  KeyIcon, 
  MicIcon, 
  VideoIcon, 
  ArrowRightIcon,
  GraduationCapIcon,
  UsersIcon,
  BookOpenIcon,
  PlayCircleIcon,
  ClockIcon,
  CreditCardIcon,
  CodeIcon,
  PlusIcon
} from 'lucide-react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import BetaAccessModal from '../components/modals/BetaAccessModal';
import { useAPIKey } from '../contexts/APIKeyContext';
import { useAuth } from '../contexts/AuthContext';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { apiKey } = useAPIKey();
  const [showBetaModal, setShowBetaModal] = useState(false);

  const handleStartSession = () => {
    if (!user) {
      navigate('/subscription');
      return;
    }
    navigate('/session');
  };

  const handleAccessBeta = () => {
    if (!user) {
      navigate('/subscription');
      return;
    }
    if (apiKey) {
      navigate('/session');
    } else {
      setShowBetaModal(true);
    }
  };

  const ecosystemApps = [
    {
      name: 'AI Learning Hub',
      description: 'Curated courses and AI-guided study tools.',
      icon: GraduationCapIcon,
      url: 'https://learninghub.aichaperones.com/',
    },
    {
      name: 'AI Tutor Hub',
      description: 'Analyze documents and videos using AI to enhance learning.',
      icon: BookOpenIcon,
      url: 'https://hub.aichaperones.com/',
    },
    {
      name: 'AI Community',
      description: 'Collaborate and share insights to grow with others.',
      icon: UsersIcon,
      url: 'https://aichaperones.com/community',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700">
      <Helmet>
        <title>VoiceVision – AI-Powered Voice + Vision for the Web</title>
        <meta name="description" content="Experience the future of web interaction with advanced voice transcription, GPT-4 Vision analysis, and natural speech synthesis." />
        <meta property="og:title" content="VoiceVision – AI Voice + Vision Assistant" />
        <meta property="og:description" content="Join the VoiceVision beta. Advanced AI-powered voice and vision interaction." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Navigation */}
      <nav className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <MicIcon className="h-8 w-8 text-white" />
            <span className="ml-2 text-2xl font-bold text-white">VoiceVision</span>
          </div>
          <div className="flex items-center space-x-4">
            {user ? (
              <>
                <button
                  onClick={() => navigate('/app')}
                  className="text-white hover:text-white/80 transition-colors"
                >
                  Dashboard
                </button>
                <button
                  onClick={() => navigate('/session')}
                  className="bg-white/20 text-white px-4 py-2 rounded-lg hover:bg-white/30 transition-colors"
                >
                  Start Session
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/login')}
                  className="text-white hover:text-white/80 transition-colors"
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/signup')}
                  className="bg-white/20 text-white px-4 py-2 rounded-lg hover:bg-white/30 transition-colors"
                >
                  Sign Up
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="container mx-auto px-4"
      >
        {/* Hero Section */}
        <section className="py-16 text-center text-white">
          <motion.div variants={itemVariants}>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              AI-Powered Voice + Vision for the Web
            </h1>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Experience the future of web interaction with advanced voice transcription,
              GPT-4 Vision analysis, and natural speech synthesis.
            </p>
            <div className="max-w-md mx-auto mb-12">
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate('/subscription')}
                className="bg-gradient-to-r from-secondary-500 to-primary-500 hover:from-secondary-600 hover:to-primary-600 shadow-lg hover:shadow-xl transition-all"
                leftIcon={<KeyIcon className="h-5 w-5" />}
              >
                Get Started Free
              </Button>
            </div>
          </motion.div>
        </section>

        {/* Feature Cards */}
        <section className="py-8">
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div variants={itemVariants}>
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-colors h-full">
                <div className="p-6 text-white">
                  <MicIcon className="h-10 w-10 mb-4" />
                  <h3 className="text-xl font-bold mb-2">Voice Transcription</h3>
                  <p className="text-white/80">
                    Crystal-clear voice transcription powered by OpenAI's Whisper model.
                  </p>
                </div>
              </Card>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-colors h-full">
                <div className="p-6 text-white">
                  <VideoIcon className="h-10 w-10 mb-4" />
                  <h3 className="text-xl font-bold mb-2">GPT-4 Vision</h3>
                  <p className="text-white/80">
                    Advanced visual understanding and analysis using GPT-4's vision capabilities.
                  </p>
                </div>
              </Card>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-colors h-full">
                <div className="p-6 text-white">
                  <KeyIcon className="h-10 w-10 mb-4" />
                  <h3 className="text-xl font-bold mb-2">BYOK Support</h3>
                  <p className="text-white/80">
                    Bring Your Own Key (BYOK) - Use your personal OpenAI API key for unlimited sessions, complete control over your usage, and no subscription limits. Perfect for power users who want maximum flexibility.
                  </p>
                </div>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* AI Chaperones Ecosystem */}
        <section className="py-16">
          <div className="text-center mb-12">
            <motion.div variants={itemVariants}>
              <h2 className="text-3xl font-bold text-white mb-4">
                Part of the AI Chaperones Ecosystem
              </h2>
              <p className="text-xl text-white/80">
                Discover our suite of AI-powered learning and collaboration tools
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {ecosystemApps.map((app, index) => (
              <motion.div 
                key={app.name}
                variants={itemVariants}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-colors h-full">
                  <a
                    href={app.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-6 text-white"
                  >
                    <app.icon className="h-10 w-10 mb-4" />
                    <h3 className="text-xl font-bold mb-2">{app.name}</h3>
                    <p className="text-white/80 mb-4">{app.description}</p>
                    <div className="flex items-center text-sm font-medium">
                      Visit <ArrowRightIcon className="h-4 w-4 ml-1" />
                    </div>
                  </a>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Get Started Section */}
        <section className="py-16">
          <motion.div variants={itemVariants} className="text-center">
            <h2 className="text-3xl font-bold text-white mb-8">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Join thousands of users already experiencing the future of AI-powered interaction.
              Use our subscription plans or bring your own OpenAI API key for unlimited access.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate('/subscription')}
                className="bg-gradient-to-r from-secondary-500 to-primary-500 hover:from-secondary-600 hover:to-primary-600"
                leftIcon={<PlayCircleIcon className="h-5 w-5" />}
              >
                {user ? 'Start Session' : 'Get Started Free'}
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/subscription')}
                className="border-white/30 text-white hover:bg-white/10"
                leftIcon={<CreditCardIcon className="h-5 w-5" />}
              >
                View Plans
              </Button>
            </div>
            
            {/* BYOK Information */}
            <div className="mt-8 p-6 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 max-w-2xl mx-auto">
              <div className="flex items-center justify-center mb-4">
                <KeyIcon className="h-6 w-6 text-white mr-2" />
                <h3 className="text-lg font-semibold text-white">Bring Your Own Key (BYOK)</h3>
              </div>
              <p className="text-white/80 text-sm mb-4">
                Already have an OpenAI API key? Use it for unlimited sessions regardless of your subscription plan. 
                Perfect for developers, power users, and teams who want complete control over their AI usage.
              </p>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-white/70">
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                  Unlimited session time
                </div>
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                  No daily limits
                </div>
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                  Direct OpenAI billing
                </div>
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                  Works with any plan
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Footer */}
        <footer className="py-8 mt-16 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center text-white/60">
            <div className="text-sm">
              © {new Date().getFullYear()} VoiceVision. All rights reserved.
            </div>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <button 
                onClick={() => navigate('/privacy')} 
                className="hover:text-white transition-colors"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => navigate('/terms')} 
                className="hover:text-white transition-colors"
              >
                Terms of Service
              </button>
              <a href="mailto:support@aichaperones.com" className="hover:text-white transition-colors">
                Contact
              </a>
            </div>
          </div>
        </footer>
      </motion.div>

      <BetaAccessModal
        isOpen={showBetaModal}
        onClose={() => setShowBetaModal(false)}
      />
    </div>
  );
};

export default Home;